import { computed, type ComputedRef, ref } from 'vue';

export const SnackbarStatus = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info'
} as const;

type StatusTypes = (typeof SnackbarStatus)[keyof typeof SnackbarStatus];

const Icon: Record<StatusTypes, string> = {
  [SnackbarStatus.SUCCESS]: '$success',
  [SnackbarStatus.ERROR]: '$clear',
  [SnackbarStatus.INFO]: '$info'
};

interface IconTypes {
  color: StatusTypes;
  icon: string;
}

interface ShowSnackbarOptions {
  message: string;
  status: StatusTypes;
  timeout?: number | undefined;
}

const DEFAULT_TIMEOUT = 3000;

const isSnackbarVisible = ref<boolean>(false);
const message = ref<string>('');
const status = ref<StatusTypes>(SnackbarStatus.INFO);
const timeout = ref<number>(DEFAULT_TIMEOUT);

interface SnackbarMessage {
  isSnackbarVisible: typeof isSnackbarVisible;
  message: typeof message;
  status: typeof status;
  timeout: typeof timeout;
  icon: ComputedRef<IconTypes>;
  hasCloseBtn: ComputedRef<boolean>;
  showSnackbar: (options: ShowSnackbarOptions) => void;
  showSuccess: (message: string, timeout?: number) => void;
  showError: (message: string, timeout?: number) => void;
  showInfo: (message: string, timeout?: number) => void;
  closeSnackbar: () => void;
}

/**
 * Global snackbar: white surface, top right, status icon, auto-dismisses after
 * 3s. Pass a `timeout` of `-1` to keep it open with a close button. Rendered
 * once in `App.vue`.
 *
 * `message` may contain simple HTML (e.g. a bold span); it is sanitised.
 *
 * @example
 * const { showSuccess, showError } = useSnackbar();
 * showSuccess(t('login.mfa.codeCopied'));
 * showError('Unable to parse JSON', 7000);
 * showError('Upload failed', -1); // stays open until closed
 */
export const useSnackbar = (): SnackbarMessage => {
  const showSnackbar = ({
    message: msg,
    status: newStatus,
    timeout: newTimeout = DEFAULT_TIMEOUT
  }: ShowSnackbarOptions) => {
    message.value = msg;
    status.value = newStatus;
    timeout.value = newTimeout;
    isSnackbarVisible.value = true;
  };

  const showSuccess = (msg: string, newTimeout?: number) =>
    showSnackbar({
      message: msg,
      status: SnackbarStatus.SUCCESS,
      timeout: newTimeout
    });

  const showError = (msg: string, newTimeout?: number) =>
    showSnackbar({
      message: msg,
      status: SnackbarStatus.ERROR,
      timeout: newTimeout
    });

  const showInfo = (msg: string, newTimeout?: number) =>
    showSnackbar({
      message: msg,
      status: SnackbarStatus.INFO,
      timeout: newTimeout
    });

  const closeSnackbar = () => {
    isSnackbarVisible.value = false;
  };

  const icon = computed<IconTypes>(() => ({
    color: status.value,
    icon: Icon[status.value]
  }));

  // v-snackbar only treats -1 as "never close"
  const hasCloseBtn = computed(() => timeout.value === -1);

  return {
    isSnackbarVisible,
    message,
    status,
    timeout,
    icon,
    hasCloseBtn,
    showSnackbar,
    showSuccess,
    showError,
    showInfo,
    closeSnackbar
  };
};
